import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { cn } from '@/lib/shadcn/utils';

import { AutomationChip } from './AutomationChip';
import { type Setup, setups, stageKeys, stageLabels } from './jobAutomation';

// The header and every row share these columns so the chips line up under their labels.
const columns =
  'sm:grid sm:grid-cols-[16px_minmax(0,1fr)_140px_140px] sm:items-start sm:gap-x-4';

interface SetupPickerProps {
  /** The chosen setup; undefined checks none. */
  value: Setup['key'] | undefined;
  /** The job's saved setup, marked Current; undefined when it matches none. */
  current: Setup['key'] | undefined;
  onChange: (setup: Setup) => void;
  /** Locks the choice while a save is in flight, so a second save cannot race it. */
  disabled?: boolean;
}

/**
 * The setups as one radio group with who handles each stage beside them.
 * Choosing only stages a change: Radix moves the choice with the arrow keys,
 * so saving waits for an explicit review.
 */
export function SetupPicker({
  value,
  current,
  onChange,
  disabled,
}: SetupPickerProps) {
  return (
    <div className="flex flex-col gap-2">
      <div
        aria-hidden
        className={cn(
          columns,
          'hidden px-5 text-[11px] font-medium tracking-[0.02em] text-neutral-500',
        )}
      >
        <span className="col-start-2">Setup</span>
        {stageKeys.map((key) => (
          <span key={key}>{stageLabels[key]}</span>
        ))}
      </div>
      <RadioGroupPrimitive.Root
        aria-label="How Finlay runs this job"
        className="flex flex-col gap-2"
        disabled={disabled}
        onValueChange={(key) => {
          const setup = setups.find((candidate) => candidate.key === key);
          if (setup) onChange(setup);
        }}
        value={value ?? ''}
      >
        {setups.map((setup) => (
          <SetupRow
            isActive={setup.key === value}
            isCurrent={setup.key === current}
            key={setup.key}
            setup={setup}
          />
        ))}
      </RadioGroupPrimitive.Root>
    </div>
  );
}

interface SetupRowProps {
  setup: Setup;
  isActive: boolean;
  isCurrent: boolean;
}

function SetupRow({ setup, isActive, isCurrent }: SetupRowProps) {
  return (
    <RadioGroupPrimitive.Item
      className={cn(
        columns,
        'focus-visible:ring-ring/50 flex cursor-pointer gap-3 rounded-[10px] border px-5 py-[18px] text-left transition-colors transition-opacity outline-none focus-visible:ring-[3px] disabled:cursor-wait disabled:opacity-70',
        isActive
          ? 'border-primary bg-secondary-150/40 shadow-[inset_0_0_0_0.5px_var(--color-primary)]'
          : 'bg-background hover:border-input',
      )}
      value={setup.key}
    >
      <span
        className={cn(
          'mt-0.5 size-4 shrink-0 rounded-full bg-white transition-[border-width]',
          isActive
            ? 'border-primary border-[5px]'
            : 'border-input border-[1.5px]',
        )}
      />
      <span className="flex min-w-0 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold">{setup.title}</span>
          {setup.recommended && (
            <span className="text-primary-700 text-[11px] font-medium">
              Recommended
            </span>
          )}
          {isCurrent && (
            <span className="bg-muted text-muted-foreground rounded-full px-[7px] py-0.5 text-[11px] font-medium">
              Current
            </span>
          )}
        </span>
        <span className="text-muted-foreground text-[13px] leading-[1.5]">
          {setup.description}
        </span>
        {/* Below sm the columns collapse, so each chip carries its own label. */}
        <span className="mt-2 flex flex-wrap gap-x-4 gap-y-2 sm:hidden">
          {stageKeys.map((key) => (
            <span className="flex items-center gap-2" key={key}>
              <span className="text-xs text-neutral-500">
                {stageLabels[key]}
              </span>
              <AutomationChip stage={key} values={setup.values} />
            </span>
          ))}
        </span>
      </span>
      {stageKeys.map((key) => (
        <span className="hidden sm:block" key={key}>
          <AutomationChip stage={key} values={setup.values} />
        </span>
      ))}
    </RadioGroupPrimitive.Item>
  );
}
